import { createAuthAxios, handleApiError } from "../../utils/apiUtils";
import { API_BASE_URL } from "../../config/apiConfig";

/**
 * Core room operations service
 * Handles room CRUD, membership, and basic operations
 */
class RoomService {
  /**
   * Get available research fields and subfields
   * PUBLIC endpoint - no auth required
   */
  async getResearchFields() {
    try {
      const response = await fetch(`${API_BASE_URL}/research-rooms/fields`);
      return await response.json();
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get all research rooms with optional filters
   * @param {Object} filters - { page, limit, field, subField, search }
   */
  async getAllRooms(filters = {}) {
    try {
      const authAxios = createAuthAxios();
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.field) params.append("field", filters.field);
      if (filters.subField) params.append("subField", filters.subField);
      if (filters.search) params.append("search", filters.search);

      const url = `${API_BASE_URL}/research-rooms${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get user's joined research rooms
   * @param {String} role - "leader", "member", or "all" (default: "all")
   */
  async getMyRooms(role = "all") {
    try {
      const authAxios = createAuthAxios();
      const url = `${API_BASE_URL}/research-rooms/my-rooms${
        role !== "all" ? `?role=${role}` : ""
      }`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get a specific research room by ID
   * @param {String} roomId - The room ID
   */
  async getRoomById(roomId) {
    try {
      if (!roomId) {
        throw new Error("Lab ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/${roomId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Create a new research room
   * @param {Object} roomData - { name, description, field, subField, photoURL }
   */
  async createRoom(roomData) {
    try {
      // Validate required fields
      if (!roomData.name || roomData.name.length < 15) {
        throw new Error("Lab name must be at least 15 characters");
      }
      if (!roomData.description || roomData.description.length < 30) {
        throw new Error("Lab description must be at least 30 characters");
      }
      if (!roomData.field) {
        throw new Error("Field is required");
      }
      if (!roomData.subField) {
        throw new Error("Subfield is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms`,
        roomData
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Send a request to join a room
   * @param {String} roomId - The room ID
   * @param {String} message - Optional message for the request
   */
  async requestToJoinRoom(roomId, message = "") {
    try {
      if (!roomId) {
        throw new Error("Lab ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/${roomId}/join`,
        { message }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Leave a research room
   * @param {String} roomId - The room ID
   */
  async leaveRoom(roomId) {
    try {
      if (!roomId) {
        throw new Error("Lab ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/${roomId}/leave`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Handle join request (Leader only)
   * @param {String} roomId - The room ID
   * @param {String} userId - The user ID
   * @param {String} action - "accept" or "reject"
   */
  async handleJoinRequest(roomId, userId, action) {
    try {
      if (!roomId || !userId || !action) {
        throw new Error("Lab ID, User ID, and action are required");
      }
      if (!["accept", "reject"].includes(action)) {
        throw new Error('Action must be "accept" or "reject"');
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/${roomId}/requests/${userId}`,
        { action }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Remove a member from the room (Leader only)
   * @param {String} roomId - The room ID
   * @param {String} userId - The user ID to remove
   */
  async removeMember(roomId, userId) {
    try {
      if (!roomId || !userId) {
        throw new Error("Lab ID and User ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${API_BASE_URL}/research-rooms/${roomId}/members/${userId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update room settings (Leader only)
   * @param {String} roomId - The room ID
   * @param {Object} updates - Room data to update
   */
  async updateRoom(roomId, updates) {
    try {
      if (!roomId) {
        throw new Error("Lab ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${API_BASE_URL}/research-rooms/${roomId}`,
        updates
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete a room (Leader only)
   * @param {String} roomId - The room ID
   */
  async deleteRoom(roomId) {
    try {
      if (!roomId) {
        throw new Error("Lab ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${API_BASE_URL}/research-rooms/${roomId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default new RoomService();
